import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Copy, Check, BookOpen, AlertTriangle, KeyRound, Bell } from 'lucide-react';
import { appApi } from '../utils/api';
import type { AppInfo } from '../types/api';

const CodeBlock: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };
  return (
    <div className="relative bg-black/60 border border-zinc-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/60">
        <span className="text-zinc-500 text-xs font-mono uppercase">{language}</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-zinc-500 hover:text-white text-xs transition-colors"
        >
          {copied ? <Check size={14} className="text-emerald-400" /> : <Copy size={14} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="p-4 overflow-x-auto text-[13px] leading-relaxed text-zinc-300 font-mono">
        {code}
      </pre>
    </div>
  );
};

const Section: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({
  title,
  id,
  children,
}) => (
  <section id={id} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
    <h2 className="text-lg font-semibold text-white mb-4">{title}</h2>
    <div className="space-y-4">{children}</div>
  </section>
);

const DataTable: React.FC<{ headers: string[]; rows: (string | React.ReactNode)[][] }> = ({
  headers,
  rows,
}) => (
  <div className="overflow-x-auto rounded-lg border border-zinc-800">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-zinc-800/60 border-b border-zinc-700">
          {headers.map((h) => (
            <th key={h} className="text-left text-zinc-300 font-medium px-4 py-2.5 whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="border-b border-zinc-800 last:border-0">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-2.5 text-zinc-400 align-top">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ApiDocsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    (async () => {
      const preselectId = Number(searchParams.get('app_id')) || null;
      try {
        const res = await appApi.getList(1, 100);
        if (res.code === 1 && res.data) {
          const list = Array.isArray(res.data.data) ? res.data.data : [];
          setApps(list);
          const target = list.find((a) => a.id === preselectId) || list[0];
          setSelectedId(target?.id ?? null);
        }
      } catch (err) {
        console.error('Failed to fetch apps:', err);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const app = apps.find((a) => a.id === selectedId) || null;

  const apiBase = 'https://your-domain.com/addons/dramas';
  const appid = app?.appid || 'your-app-id';
  const appkey = app?.appkey || 'your-app-key';

  const phpRequest = `<?php
$appkey = '${appkey}';
$appid = '${appid}';
$timestamp = time();
$nonce = bin2hex(random_bytes(16));

$bodyJson = json_encode([
    'out_trade_no' => 'M20260824001',
    'amount' => 19.99,
    'currency' => 'USD',
    'subject' => 'VIP Subscription',
    'return_url' => 'https://merchant.com/pay/return',
    'notify_url' => 'https://merchant.com/pay/notify',
]);

$path = '/payapi/create';
$method = 'POST';

$payload = "\${timestamp}\\n\${nonce}\\n\${method}\\n\${path}\\n" . hash('sha256', $bodyJson);
$sign = hash_hmac('sha256', $payload, $appkey);

$headers = [
    "X-Appid: {$appid}",
    "X-Timestamp: {$timestamp}",
    "X-Nonce: {$nonce}",
    "X-Sign: {$sign}",
    "Content-Type: application/json",
];

$ch = curl_init("${apiBase}/payapi/create");
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, $bodyJson);
curl_setopt($ch, CURLOPT_HTTPHEADER, $headers);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
$response = curl_exec($ch);
curl_close($ch);

echo $response;
`;

  const nodeRequest = `const crypto = require('crypto');

const APPID = '${appid}';
const APPKEY = '${appkey}';

async function request(method, path, body = null) {
    const timestamp = String(Math.floor(Date.now() / 1000));
    const nonce = crypto.randomBytes(16).toString('hex');
    const bodyStr = body === null ? '' : JSON.stringify(body);
    const bodyHash = crypto.createHash('sha256').update(bodyStr).digest('hex');

    const payload = \`\${timestamp}\\n\${nonce}\\n\${method.toUpperCase()}\\n\${path}\\n\${bodyHash}\`;
    const sign = crypto.createHmac('sha256', APPKEY).update(payload).digest('hex');

    const res = await fetch('${apiBase}' + path, {
        method,
        headers: {
            'X-Appid': APPID,
            'X-Timestamp': timestamp,
            'X-Nonce': nonce,
            'X-Sign': sign,
            ...(body && { 'Content-Type': 'application/json' }),
        },
        body: body ? JSON.stringify(body) : undefined,
    });
    return res.json();
}

request('POST', '/payapi/create', {
    out_trade_no: 'M20260824001',
    amount: 19.99,
    currency: 'USD',
    subject: 'VIP Subscription',
    return_url: 'https://merchant.com/pay/return',
    notify_url: 'https://merchant.com/pay/notify',
}).then(console.log);
`;

  const pythonRequest = `import hmac, hashlib, time, secrets, requests

APPID = '${appid}'
APPKEY = b'${appkey}'
BASE_URL = '${apiBase}'

def sign_request(method, path, body=None):
    timestamp = str(int(time.time()))
    nonce = secrets.token_hex(16)
    body_str = '' if body is None else body
    body_hash = hashlib.sha256(body_str.encode()).hexdigest()
    payload = f"{timestamp}\\n{nonce}\\n{method.upper()}\\n{path}\\n{body_hash}"
    sign = hmac.new(APPKEY, payload.encode(), hashlib.sha256).hexdigest()
    return {'X-Appid': APPID, 'X-Timestamp': timestamp, 'X-Nonce': nonce, 'X-Sign': sign}

body = '{"out_trade_no":"M20260824001","amount":19.99,"currency":"USD","subject":"VIP Subscription"}'
headers = sign_request('POST', '/payapi/create', body)
headers['Content-Type'] = 'application/json'

resp = requests.post(f'{BASE_URL}/payapi/create', headers=headers, data=body)
print(resp.json())
`;

  const curlCreate = `curl -X POST ${apiBase}/payapi/create \\
  -H "Content-Type: application/json" \\
  -H "X-Appid: ${appid}" \\
  -H "X-Timestamp: $(date +%s)" \\
  -H "X-Nonce: abcdefgh12345678" \\
  -H "X-Sign: <your-hex-sign>" \\
  -d '{
    "out_trade_no": "M20260824001",
    "amount": 19.99,
    "currency": "USD",
    "subject": "VIP Subscription",
    "return_url": "https://merchant.com/pay/return",
    "notify_url": "https://merchant.com/pay/notify"
  }'`;

  const phpVerifyNotify = `<?php
$appkey = '${appkey}';
$body = file_get_contents('php://input');
$ts = $_SERVER['HTTP_X_TIMESTAMP'];
$nonce = $_SERVER['HTTP_X_NONCE'];
$sign = $_SERVER['HTTP_X_SIGN'];
$path = parse_url($_SERVER['REQUEST_URI'], PHP_URL_PATH);

$payload = "\${ts}\\n\${nonce}\\nPOST\\n\${path}\\n" . hash('sha256', $body);
$expected = hash_hmac('sha256', $payload, $appkey);

if (!hash_equals($expected, strtolower($sign))) {
    http_response_code(400);
    exit('sign invalid');
}

$data = json_decode($body, true);
if ($data['status'] != '1') {
    exit('not paid');
}

// 业务处理（发货/开卡等）...

echo 'success'; // 返回 2xx 即视为已收到
`;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
          <BookOpen size={24} className="text-[#635BFF]" />
          API Documentation
        </h1>
        <p className="text-zinc-400 mt-1">
          Integrate the payment gateway into your system. Create a payment link, receive results via
          callback and server notification.
        </p>
      </div>

      {/* App selector */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
        <label className="block text-zinc-400 text-sm mb-2">
          Application <span className="text-zinc-600">(code samples below use its AppID / AppKey)</span>
        </label>
        {loading ? (
          <div className="text-zinc-500 text-sm">Loading...</div>
        ) : apps.length === 0 ? (
          <div className="text-amber-400 text-sm">
            No applications yet. Create one in the Applications page first to get your AppID / AppKey.
          </div>
        ) : (
          <select
            value={selectedId ?? ''}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            className="w-full sm:w-96 px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-white focus:outline-none focus:border-zinc-600 transition-colors"
          >
            {apps.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Credentials */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <KeyRound size={18} className="text-[#635BFF]" />
          <h2 className="text-lg font-semibold text-white">Your Credentials</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="text-zinc-400 text-sm mb-1">AppID</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-black/60 border border-zinc-800 rounded-lg text-zinc-200 text-xs font-mono break-all">
                {appid}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(appid)}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
                title="Copy AppID"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
          <div>
            <div className="text-zinc-400 text-sm mb-1">AppKey</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-black/60 border border-zinc-800 rounded-lg text-zinc-200 text-xs font-mono break-all">
                {appkey}
              </code>
              <button
                onClick={() => navigator.clipboard.writeText(appkey)}
                className="p-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-400 hover:text-white transition-colors"
                title="Copy AppKey"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        </div>
        <p className="text-zinc-500 text-xs mt-3">
          AppID / AppKey are generated on creation and cannot be modified. Keep AppKey secret — never
          expose it in client-side code.
        </p>
      </div>

      {/* Overview */}
      <Section title="1. Overview" id="overview">
        <p className="text-zinc-400 text-sm leading-relaxed">
          The payment gateway lets your customers pay via Stripe. The flow:
        </p>
        <ol className="list-decimal list-inside text-zinc-400 text-sm space-y-1">
          <li>Call <code className="text-zinc-200 font-mono text-xs">POST /addons/dramas/payapi/create</code> (signed) to create a Stripe payment link.</li>
          <li>Redirect the payer to the returned <code className="text-zinc-200 font-mono text-xs">pay_url</code>.</li>
          <li>After payment, the browser returns to your <code className="text-zinc-200 font-mono text-xs">return_url</code> with result params (page callback).</li>
          <li>We forward the payment result to your <code className="text-zinc-200 font-mono text-xs">notify_url</code> with our signature (server notification — use this as the source of truth).</li>
        </ol>
      </Section>

      {/* Signature */}
      <Section title="2. Request Signature" id="signature">
        <p className="text-zinc-400 text-sm">
          Every request must carry these headers:
        </p>
        <DataTable
          headers={['Header', 'Description']}
          rows={[
            ['X-Appid', 'Your AppID'],
            ['X-Timestamp', 'Current Unix second timestamp (±300s tolerance)'],
            ['X-Nonce', 'Random string, 8-64 chars, unique within 10 minutes (anti-replay)'],
            ['X-Sign', 'Lowercase hex of HMAC-SHA256(appkey, payload)'],
            ['Content-Type', 'application/json for POST'],
          ]}
        />
        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 text-zinc-300 text-xs font-mono whitespace-pre-wrap">
{`payload = timestamp + "\\n" + nonce + "\\n" + METHOD + "\\n" + path + "\\n" + sha256(body)

sign = Hex(HMAC-SHA256(appkey, payload))   # lowercase

  - METHOD: uppercase HTTP method (POST / GET)
  - path:   request path without domain/addon prefix, e.g. /payapi/create
  - body:   "" for GET; the exact raw JSON body for POST`}
        </div>

        <h3 className="text-white text-sm font-medium">PHP</h3>
        <CodeBlock code={phpRequest} language="php" />

        <h3 className="text-white text-sm font-medium">JavaScript (Node.js)</h3>
        <CodeBlock code={nodeRequest} language="javascript" />

        <h3 className="text-white text-sm font-medium">Python</h3>
        <CodeBlock code={pythonRequest} language="python" />
      </Section>

      {/* Create order */}
      <Section title="3. Create Payment Order" id="create">
        <p className="text-zinc-400 text-sm">
          <code className="text-zinc-200 font-mono text-xs">POST /addons/dramas/payapi/create</code> — creates an order and a Stripe Checkout payment link.
        </p>
        <DataTable
          headers={['Field', 'Type', 'Required', 'Description']}
          rows={[
            ['out_trade_no', 'string', 'Yes', 'Your merchant order no. (unique per application, ≤64 chars)'],
            ['amount', 'float', 'Yes', 'Order amount (>0)'],
            ['currency', 'string', 'No', 'USD (default) / CNY / EUR / GBP / JPY / CAD / AUD'],
            ['subject', 'string', 'No', 'Product name'],
            ['notify_url', 'string', 'No', 'Server notification URL (defaults to app setting)'],
            ['return_url', 'string', 'No', 'Page callback URL (defaults to notify_url)'],
          ]}
        />
        <CodeBlock code={curlCreate} language="bash" />
        <div className="text-zinc-400 text-sm">
          <div className="text-white text-sm font-medium mb-1">Response</div>
          <div className="bg-black/60 border border-zinc-800 rounded-lg p-4 text-zinc-300 text-xs font-mono whitespace-pre-wrap">
{`{
    "code": 1,
    "msg": "ok",
    "time": 1787000000,
    "data": {
        "order_sn": "PO202608241236000001234567890",
        "out_trade_no": "M20260824001",
        "amount": "19.99",
        "currency": "USD",
        "pay_url": "https://checkout.stripe.com/c/pay/cs_test_..."
    }
}`}
          </div>
        </div>
        <p className="text-zinc-500 text-xs">
          Idempotent: calling again with the same pending <code className="text-zinc-300 font-mono">out_trade_no</code> returns a fresh payment link for the same order.
        </p>
      </Section>

      {/* Query */}
      <Section title="4. Query Order" id="query">
        <p className="text-zinc-400 text-sm">
          <code className="text-zinc-200 font-mono text-xs">POST /addons/dramas/payapi/query</code> — query order status by <code className="text-zinc-200 font-mono text-xs">out_trade_no</code>.
        </p>
        <DataTable
          headers={['Field', 'Type', 'Required', 'Description']}
          rows={[['out_trade_no', 'string', 'Yes', 'Your merchant order no.']]}
        />
        <DataTable
          headers={['status', 'Meaning']}
          rows={[
            ['0', 'Pending'],
            ['1', 'Paid'],
            ['2', 'Refunded'],
            ['3', 'Closed'],
          ]}
        />
      </Section>

      {/* Return callback */}
      <Section title="5. Page Callback (return_url)" id="callback">
        <p className="text-zinc-400 text-sm">
          After payment the browser is redirected to your <code className="text-zinc-200 font-mono text-xs">return_url</code> with these query params:
        </p>
        <DataTable
          headers={['Param', 'Description']}
          rows={[
            ['order_sn', 'System order number'],
            ['out_trade_no', 'Your merchant order number'],
            ['status', '0 = not paid, 1 = paid'],
            ['amount', 'Order amount'],
            ['currency', 'Currency'],
            ['sign', 'Callback signature'],
          ]}
        />
        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 text-zinc-300 text-xs font-mono whitespace-pre-wrap">
{`signStr = sorted params (excluding sign) joined as key=value (rawurlencoded) with &
sign = Hex(HMAC-SHA256(appkey, signStr))   # lowercase

Example:
  amount=19.99&currency=USD&order_sn=POxxx&out_trade_no=Mxxx&status=1`}
        </div>
        <p className="text-amber-400/90 text-xs flex items-center gap-1.5">
          <AlertTriangle size={14} />
          Page callbacks can be forged / shown for display only. Always use the server notification (notify_url) for fulfillment.
        </p>
      </Section>

      {/* Server notify */}
      <Section title="6. Server Notification (notify_url)" id="notify">
        <p className="text-zinc-400 text-sm flex items-center gap-1.5">
          <Bell size={16} />
          After payment is confirmed we POST the result to your notify_url with our signature. Verify it before fulfilling.
        </p>
        <DataTable
          headers={['Header', 'Description']}
          rows={[
            ['X-Appid', 'Application AppID'],
            ['X-Timestamp', 'Unix second timestamp'],
            ['X-Nonce', 'Random string'],
            ['X-Sign', 'Signature (lowercase hex)'],
          ]}
        />
        <div className="text-zinc-400 text-sm">
          <div className="text-white text-sm font-medium mb-1">Body (JSON)</div>
          <div className="bg-black/60 border border-zinc-800 rounded-lg p-4 text-zinc-300 text-xs font-mono whitespace-pre-wrap">
{`{
    "order_sn": "PO202608241236000001234567890",
    "out_trade_no": "M20260824001",
    "status": "1",
    "amount": "19.99",
    "currency": "USD",
    "pay_fee": "19.99",
    "transaction_id": "cs_test_...",
    "notify_time": "2026-08-24 09:37:29"
}`}
          </div>
        </div>
        <div className="bg-zinc-800/50 border border-zinc-700/50 rounded-lg p-4 text-zinc-300 text-xs font-mono whitespace-pre-wrap">
{`payload = timestamp + "\\n" + nonce + "\\n" + "POST" + "\\n" + path + "\\n" + sha256(body)
sign = Hex(HMAC-SHA256(appkey, payload))

path = the path portion of your notify_url (e.g. /pay/notify)`}
        </div>
        <h3 className="text-white text-sm font-medium">PHP verification example</h3>
        <CodeBlock code={phpVerifyNotify} language="php" />
        <p className="text-zinc-500 text-xs">
          Return HTTP 2xx to acknowledge. Non-2xx or timeout will be marked as failed and re-sent on subsequent related operations. Handle notifications idempotently (duplicates may occur).
        </p>
      </Section>

      {/* Errors */}
      <Section title="7. Common Errors" id="errors">
        <DataTable
          headers={['Message', 'Meaning']}
          rows={[
            ['缺少签名头', 'Missing X-Appid / X-Timestamp / X-Nonce / X-Sign header'],
            ['应用不存在或已禁用', 'Invalid AppID or application disabled'],
            ['签名时间戳格式错误', 'Timestamp is not numeric'],
            ['签名已过期或时间戳偏差过大', 'Clock skew > 300s'],
            ['nonce 长度非法', 'Nonce length not in 8-64'],
            ['签名校验失败', 'Signature mismatch'],
            ['nonce 已使用（重放请求）', 'Nonce already used (replay)'],
            ['out_trade_no 不能为空', 'Missing out_trade_no'],
            ['amount 必须为大于0的数字', 'Invalid amount'],
            ['商户订单号已存在', 'out_trade_no already exists (paid/closed)'],
          ]}
        />
        <p className="text-zinc-400 text-sm">
          Success response: <code className="text-zinc-200 font-mono text-xs">{'{ code: 1, msg: "ok", data: ... }'}</code>
        </p>
      </Section>

      {/* Security */}
      <Section title="8. Security Notes" id="security">
        <ul className="list-disc list-inside text-zinc-400 text-sm space-y-1.5">
          <li>Keep AppKey secret. Never hardcode it in browsers / mini-programs.</li>
          <li>Each nonce is usable only once within 10 minutes; timestamps beyond ±5 min are rejected.</li>
          <li>Verify the page callback sign only for display — rely on notify_url for fulfillment, and validate amount &amp; out_trade_no.</li>
          <li>Handle notifications idempotently by order_sn / out_trade_no.</li>
          <li>Use string / cents precision when processing amounts.</li>
        </ul>
      </Section>
    </div>
  );
};

export default ApiDocsPage;
